import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider } from "@/context/LanguageContext";
import SmoothScroll from "@/components/SmoothScroll";
import Loader from "@/components/Loader";
import CustomCursor from "@/components/CustomCursor";
import FloatingCall from "@/components/FloatingCall";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import ChiSiamo from "@/pages/ChiSiamo";
import MenuPage from "@/pages/MenuPage";
import Contatti from "@/pages/Contatti";
import LegalPage from "@/pages/LegalPage";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { err: null };
  }
  static getDerivedStateFromError(error) {
    return { err: error };
  }
  render() {
    if (this.state.err) {
      return (
        <div
          style={{
            minHeight: "100vh",
            display: "grid",
            placeItems: "center",
            background: "#050505",
            color: "#CEAA7D",
            fontFamily: "serif",
            textAlign: "center",
            padding: "2rem",
          }}
        >
          <p>Errore imprevisto — ricarica la pagina</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function PageShell({ children }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      data-testid="page-shell"
    >
      {children}
    </motion.div>
  );
}

function scrollToHash(hash) {
  const el = document.getElementById(hash.replace("#", ""));
  if (!el) return false;
  if (window.__lenis) {
    window.__lenis.scrollTo(el, { offset: -80 });
  } else {
    el.scrollIntoView({ behavior: "smooth" });
  }
  return true;
}

function AnimatedRoutes() {
  const location = useLocation();

  // first-load deep links (e.g. /contatti#prenota): wait for the intro loader
  useEffect(() => {
    if (!location.hash) return undefined;
    const t = setTimeout(() => scrollToHash(location.hash), 2000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExitComplete = () => {
    if (!location.hash || !scrollToHash(location.hash)) {
      if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
      window.scrollTo(0, 0);
    }
  };

  return (
    <AnimatePresence mode="wait" onExitComplete={handleExitComplete}>
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageShell><Home /></PageShell>} />
        <Route path="/chi-siamo" element={<PageShell><ChiSiamo /></PageShell>} />
        <Route path="/menu" element={<PageShell><MenuPage /></PageShell>} />
        <Route path="/contatti" element={<PageShell><Contatti /></PageShell>} />
        <Route path="/privacy-policy" element={<PageShell><LegalPage doc="privacy" /></PageShell>} />
        <Route path="/termini-e-condizioni" element={<PageShell><LegalPage doc="terms" /></PageShell>} />
        <Route path="/cookie-policy" element={<PageShell><LegalPage doc="cookies" /></PageShell>} />
        <Route path="*" element={<PageShell><Home /></PageShell>} />
      </Routes>
    </AnimatePresence>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <Loader />
      <CustomCursor />
      <LanguageProvider>
        <BrowserRouter>
          <SmoothScroll>
            <Header />
            <main>
              <AnimatedRoutes />
            </main>
            <Footer />
            <FloatingCall />
          </SmoothScroll>
          <Toaster position="top-center" theme="dark" richColors />
        </BrowserRouter>
      </LanguageProvider>
    </ErrorBoundary>
  );
}
